import { useEffect, useState } from 'react';
import { useCountDown } from 'ahooks';
import type { HpApiWebApiError } from '@zg-rentals/ts-hotpads-api-client';
import { v3ErrorReason } from '@/src/lib/api/client/hotpadsApiV3Client';
import { SEND_BLOCKED_REASONS } from './otpReasons';

export const RESEND_COOLDOWN_MS = 30_000;

export type UseOtpResendOptions = {
  /** Sends a new code to the same address; rejects with the v3 error when it cannot. */
  resendCode: () => Promise<unknown>;
  isSending: boolean;
  /**
   * The caller's own send already hit the per-address budget. Starts blocked for sends, the
   * same state a rejected resend would leave; the caller keeps code entry open, because a
   * code already in the inbox is still redeemable.
   */
  sendLimitReached?: boolean;
  /** A new code went out: the caller resets whatever it holds against the previous one. */
  onResent?: () => void;
  /** The send failed; `reason` is undefined when the failure carried no v3 reason. */
  onSendError?: (reason: HpApiWebApiError | undefined) => void;
};

/**
 * Resend control for a one-time code the caller has already sent once.
 *
 * Owns the cooldown, the per-address send budget state and the "sent either way" rule:
 * sendOtp answers identically whether or not the address has an account, so a success only
 * ever means the step may continue. Copy stays with the caller — this reports reasons.
 */
export function useOtpResend({
  resendCode,
  isSending,
  sendLimitReached,
  onResent,
  onSendError,
}: UseOtpResendOptions) {
  const [cooldownUntil, setCooldownUntil] = useState<number>();
  const [resendConfirmed, setResendConfirmed] = useState(false);
  // The per-address send budget is spent; no cooldown will bring it back this hour.
  const [sendBlocked, setSendBlocked] = useState(Boolean(sendLimitReached));

  const [cooldownRemaining] = useCountDown({ targetDate: cooldownUntil });

  /**
   * Starts the resend cooldown. Called when a code has actually gone out, never when one
   * is merely requested — measuring from the request would let the countdown expire while
   * the send is still in flight, leaving the control reading "Sending…" at zero seconds.
   */
  const startCooldown = () => setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);

  useEffect(() => {
    // The caller sent the first code, so the cooldown starts here rather than after a
    // send of our own. Deliberately not firing sendOtp on mount: useMutation subscribes
    // its observer in an effect, and StrictMode's mount/cleanup/mount cycle orphans a
    // mutation started during that first pass — the request goes out but its result
    // never reaches the observer, leaving isPending stuck on forever. Resend is safe
    // because it is click-driven, by which point the observer is settled.
    startCooldown();
  }, []);

  const resend = () => {
    // The control is disabled too, but the cooldown exists to protect a write
    // rate-limited endpoint — too load-bearing to leave to the button's styling.
    if (isSending || cooldownRemaining > 0 || sendBlocked) {
      return;
    }
    setResendConfirmed(false);

    resendCode().then(
      () => {
        startCooldown();
        // Reached either way: sendOtp answers identically whether or not the address
        // has an account, and the UI must not narrow that down.
        setResendConfirmed(true);
        onResent?.();
      },
      (error: unknown) => {
        // No cooldown to undo: it only starts once a code is actually out, so a failed
        // send leaves the button immediately available rather than stranding the user on
        // an error that tells them to request a new one.
        const reason = v3ErrorReason(error);
        if (reason && SEND_BLOCKED_REASONS.has(reason)) {
          setSendBlocked(true);
        }
        onSendError?.(reason);
      },
    );
  };

  const cooldownSeconds = Math.ceil(cooldownRemaining / 1000);

  return {
    resend,
    isSending,
    cooldownSeconds,
    resendDisabled: isSending || cooldownSeconds > 0 || sendBlocked,
    sendBlocked,
    resendConfirmed,
    clearConfirmation: () => setResendConfirmed(false),
  };
}
